import { observer } from "mobx-react-lite";
import Typography from "@material-ui/core/Typography";
import { Button, Card, Theme } from "@material-ui/core";
import React, { useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";
import { inject } from "mobx-react";
import { DeleteOutline } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { LoggerFactory } from "../../../logger";

const log = LoggerFactory.create("Upload");

const useStyles = makeStyles((theme: Theme) => {
  return {
    upload: {
      display: "inline-block",
      padding: theme.spacing(1) * 3
    },
    fileGrid: {
      display: "grid",
      gridGap: theme.spacing(2)
    },
    dropzone: {
      borderWidth: 2,
      borderRadius: 2,
      borderColor: "#eeeeee",
      borderStyle: "dashed",
      backgroundColor: "#fafafa",
      color: "#bdbdbd",
      minHeight: "100px",
      padding: theme.spacing(2),
      outline: "none",
      transition: "border .24s ease-in-out",
      width: "100%",
      cursor: "pointer"
    },
    dropzoneActive: {
      borderColor: "#2196f3"
    },
    dropzoneAccept: {
      borderColor: "#00e676"
    },
    dropzoneReject: {
      borderColor: "#ff1744"
    },
    dropzoneTitle: {
      marginBottom: theme.spacing(2)
    },
    submitButton: {
      marginTop: theme.spacing(1) * 2,
      width: "200px"
    }
  };
});

export interface UploadProps {
  input: {
    value: File | File[];
    onChange: (files: File | File[] | null) => void;
    [key: string]: any;
  };
  multiple?: boolean;
  readOnly?: boolean;
  accept: string[];
  [key: string]: any;
}

export const Upload = inject("i18n")(
  observer((props: UploadProps) => {
    const { i18n } = props;
    const classes = useStyles();
    const uploadInfo = i18n!.t(
      "lego:upload.info",
      "Drag & drop {{accept}} files here, or click to select files",
      { accept: fileFormat(props.accept) }
    );

    const removeFile = useCallback(
      (idx: any, file: File) => {
        if (props.multiple) {
          const currentFiles = props.input.value as File[];
          const modifiedFiles = currentFiles.filter(f => f !== file);

          props.input.onChange(modifiedFiles);
        } else {
          props.input.onChange(null);
        }
      },
      [props.input]
    );

    const fileSelected = useCallback(
      (files: File[]) => {
        log.debug("files selected", files);

        if (props.multiple) {
          const prevValue = props.input.value as File[];
          props.input.onChange([...prevValue, ...files]);
        } else {
          props.input.onChange(files[0]);
        }
      },
      [props.input]
    );

    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragAccept,
      isDragReject
    } = useDropzone({
      accept: props.accept,
      onDropAccepted: fileSelected,
      disabled: props.readOnly,
      multiple: props.multiple
    });

    const dropzoneClasses = useMemo(
      () =>
        classNames([
          classes.dropzone,
          isDragActive ? classes.dropzoneActive : "",
          isDragAccept ? classes.dropzoneAccept : "",
          isDragReject ? classes.dropzoneReject : ""
        ]),
      [
        classes.dropzone,
        classes.dropzoneActive,
        classes.dropzoneAccept,
        classes.dropzoneReject,
        isDragActive,
        isDragAccept,
        isDragReject
      ]
    );

    let files: File[];
    if (props.multiple) {
      files = (props.input.value || []) as File[];
    } else {
      if (props.input.value) {
        files = [props.input.value as File];
      } else {
        files = [];
      }
    }

    return (
      <div {...getRootProps({ className: dropzoneClasses })}>
        <input {...getInputProps()} />
        {files.length === 0 && (
          <Typography variant={"h5"} className={classes.dropzoneTitle}>
            {uploadInfo}
          </Typography>
        )}
        <section className={classes.fileGrid}>
          {files.map((file, idx) => {
            return (
              <FileCard
                key={idx}
                fileKey={idx + ""}
                file={file}
                onRemove={removeFile}
              />
            );
          })}
        </section>
      </div>
    );
  })
);

interface FileCardProps {
  fileKey: any;
  file: File;
  onRemove: (key: string, file: File) => void;
}

const useFileCardStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      display: "inline-block",
      marginBottom: theme.spacing(1),
      cursor: "default",
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    deleteButton: {
      float: "right"
    }
  };
});

const FileCard = (props: FileCardProps) => {
  const classes = useFileCardStyles();

  return (
    <Card className={classes.root} onClick={e => e.stopPropagation()}>
      <Typography variant={"h5"}>
        {props.file.name}
        <Button
          className={classes.deleteButton}
          color={"primary"}
          variant={"text"}
          onClick={() => props.onRemove(props.fileKey, props.file)}
        >
          <DeleteOutline />
        </Button>
      </Typography>
    </Card>
  );
};

function fileFormat(accept: string[]) {
  return accept.map(a => a.split("/")[1]);
}
